// Sentry 前端錯誤監控設定
//
// 整合 Sentry Browser SDK 進行前端 JavaScript 錯誤追蹤。
// 僅在 production 環境初始化，DSN 從 HTML meta tag 讀取。
//
// 使用方式：在 application.js 中 import 'utils/sentry'

import * as Sentry from '@sentry/browser'

// 檢查是否為 production 環境
const railsEnv = document.querySelector('meta[name="rails-env"]')?.content
const isProduction = railsEnv === 'production'

/**
 * 自家 bundle 的網址前綴，作為 Sentry 的 allowUrls：只回報自家程式丟出的錯誤。
 *
 * Chrome iOS 會把自己的腳本注入頁面，那些腳本的錯誤（RangeError: Maximum call stack size exceeded、
 * 被混淆成 "Error: La" 之類的訊息）掛在頁面網址上，denyUrls 擋不到，訊息與函式名稱又隨 Chrome 版本改變。
 * 它們每個月都把整個 org 共用的錯誤額度吃光，之後連後端的錯誤都進不了 Sentry
 * （docs/prefect_operations.md「2026-10-18：前端錯誤『暴增』」）。
 *
 * Sentry 用例外最內層 frame 的檔案網址比對 allowUrls（字串是子字串比對）。自家程式都由 webpack 打包、
 * 從 publicPath 載入，所以前綴取自 webpack 執行期的 publicPath，不寫死 '/packs/' 或網域：輸出路徑、網域或
 * asset host 改變時會跟著變，不會靜默丟掉自家的錯誤。頁面上的 inline script（追蹤碼等）丟出的錯誤不會回報。
 *
 * @param {string|undefined} publicPath - webpack 的 publicPath（例如 '/packs/'；設定 asset host 時是完整網址）
 * @param {string} baseUrl - 解析相對 publicPath 用的網址（目前頁面）
 * @returns {string[]} allowUrls；publicPath 不可用時回傳空陣列，不限制來源：寧可雜訊回來，也不要漏掉自家的錯誤
 */
export function firstPartyScriptUrls(publicPath, baseUrl) {
  if (typeof publicPath !== 'string' || publicPath === '') {
    return []
  }
  return [new URL(publicPath, baseUrl).href]
}

/**
 * 組出 production 的 Sentry.init 設定。
 *
 * 獨立成函式是為了讓測試能把同一份設定交給真正的 SDK，驗證哪些錯誤會被送出
 * （spec/javascript/sentry_filtering_spec.js）。
 *
 * @param {Object} params
 * @param {string} params.dsn - Sentry 前端 DSN
 * @param {string|undefined} params.release - release 版本（git commit）
 * @param {string|undefined} params.publicPath - webpack 的 publicPath，見 firstPartyScriptUrls
 * @param {string} params.baseUrl - 目前頁面的網址
 * @returns {Object} Sentry.init 的設定
 */
export function buildSentryOptions({ dsn, release, publicPath, baseUrl }) {
  return {
    dsn,
    environment: 'production',
    release,

    // 100% 捕捉所有錯誤
    sampleRate: 1.0,

    // Performance monitoring（前端不需要太多 traces，已有 New Relic）
    tracesSampleRate: 0.05,

    // Breadcrumbs 設定
    maxBreadcrumbs: 50,

    // 只回報自家 bundle 丟出的錯誤（見 firstPartyScriptUrls）
    allowUrls: firstPartyScriptUrls(publicPath, baseUrl),

    // 過濾雜訊：常見的第三方錯誤和網路問題
    ignoreErrors: [
      // 常見的瀏覽器擴充套件錯誤
      /ResizeObserver loop limit exceeded/,
      /ResizeObserver loop completed with undelivered notifications/,
      // 跨來源腳本錯誤（無法取得詳細資訊）
      /^Script error\.?$/,
      // 網路相關錯誤（通常是使用者網路問題）
      /Network Error/,
      /Failed to fetch/,
      /Load failed/,
      /NetworkError/,
      // 第三方追蹤腳本
      /gtag/,
      /google-analytics/,
      /googletagmanager/,
    ],

    // 過濾來源：排除第三方腳本和瀏覽器擴充套件（allowUrls 不可用時仍然有效）
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
      // 第三方追蹤腳本
      /gtag\/js/,
      /googletagmanager\.com/,
      /google-analytics\.com/,
      /analytics\.js/,
    ],
  }
}

// 僅在 production 環境初始化
if (isProduction) {
  const dsn = document.querySelector('meta[name="sentry-dsn"]')?.content
  const release = document.querySelector('meta[name="sentry-release"]')?.content

  if (dsn) {
    Sentry.init(buildSentryOptions({
      dsn,
      release,
      // webpack 會把這個自由變數換成執行期的 publicPath；沒有經過 webpack（例如 Jest）時不存在
      publicPath: typeof __webpack_public_path__ === 'string' ? __webpack_public_path__ : undefined,
      baseUrl: window.location.href,
    }))
  }
}

// 匯出 Sentry 供其他模組使用
export { Sentry, isProduction }
